import { motion } from 'framer-motion'
import { Link, Routes, Route } from 'react-router-dom'
import { Building, Newspaper, Scale, ArrowRight, MapPin, Users, Target, Lightbulb } from 'lucide-react'
import { navigationData } from '../utils/navigation'
import PrivacyPolicyPage from './legal/PrivacyPolicyPage'
import TermsPage from './legal/TermsPage'
import CompliancePage from './legal/CompliancePage'
import CookiesPolicyPage from './legal/CookiesPolicyPage'
import GraffitiOSPrivacyPage from './legal/GraffitiOSPrivacyPage'

const corporateCategories = [
  {
    icon: Building,
    title: 'Company',
    description: 'Learn about our mission, meet our leadership, and explore career opportunities.',
    href: '/corporate/company',
    gradient: 'from-synth-500 to-synth-700',
  },
  {
    icon: Newspaper,
    title: 'News',
    description: 'Press releases, blog posts, and upcoming events.',
    href: '/corporate/news',
    gradient: 'from-signal-500 to-synth-600',
  },
  {
    icon: Scale,
    title: 'Legal',
    description: 'Terms of service, privacy policy, and compliance information.',
    href: '/corporate/legal',
    gradient: 'from-ember-500 to-synth-600',
  },
]

const values = [
  {
    icon: Target,
    title: 'Universal Accessibility',
    description: 'AI for people of ANY socioeconomic class—inexpensively, safely, privately, with transparency and compliance.',
  },
  {
    icon: Users,
    title: 'Privacy by Design',
    description: 'No export of personal, health, or financial data to ANY third party. Your data stays inside your home or company.',
  },
  {
    icon: Lightbulb,
    title: 'Responsible Innovation',
    description: 'Open-source foundations with ethical profitability. Setting the tone for the Age of Intelligent Machines.',
  },
]

function CorporateIndex() {
  return (
    <div className="pt-24 pb-16">
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold text-void-50 mb-6">
              About <span className="text-gradient">Mimeograph Holdings</span>
            </h1>
            <p className="text-xl text-void-400 max-w-2xl mx-auto">
              Setting the tone and character of the Age of Intelligent Machines—with AI that lives safely inside your homes and companies.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {corporateCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={category.href}
                  className="group block h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${category.gradient} mb-6`}>
                    <category.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">
                    {category.title}
                  </h2>
                  <p className="text-void-400">{category.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-16 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-display font-bold text-void-50 mb-4">Our Values</h2>
            <p className="text-void-400 max-w-xl mx-auto">
              The principles that guide everything we build.
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex p-4 rounded-2xl bg-synth-900/50 border border-synth-800/30 mb-4">
                  <value.icon className="w-8 h-8 text-synth-400" />
                </div>
                <h3 className="text-xl font-display font-bold text-void-100 mb-2">{value.title}</h3>
                <p className="text-void-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Company Overview */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-synth-900/50 to-void-900 border border-synth-800/30"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-display font-bold text-void-50 mb-4">
                  The Age of Intelligent Machines
                </h2>
                <p className="text-void-400 mb-6 leading-relaxed">
                  We're a small, hands-on team — not a large agency, and we're not pretending to
                  be one. Between us, we bring decades of combined, hands-on experience across
                  CDP, private health data, identity systems, and forensic technical work on
                  projects around the world. That experience is why Graffiti™ and 23Controls™
                  are built the way they are.
                </p>
                <p className="text-void-400 mb-6 leading-relaxed">
                  Graffiti™ (Mimeograffiti) is an Open-Source, responsibly profitable, and ethically
                  devised PaaS offering which through a unique networking approach, can safely live
                  inside your homes or inside your companies—without the need for any export or
                  transfer of personal, health, or financial data to ANY third party.
                </p>
                <Link to="/philosophy" className="btn-primary inline-flex items-center gap-2">
                  Read Our Philosophy
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="space-y-4">
                <div className="p-6 rounded-2xl bg-void-950/80 border border-void-700/30">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-lg bg-synth-500/20">
                      <MapPin className="w-5 h-5 text-synth-400" />
                    </div>
                    <div>
                      <p className="text-void-300 font-medium">Headquarters</p>
                      <p className="text-void-500 text-sm">Scranton, PA, USA</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-signal-500/20">
                      <Users className="w-5 h-5 text-signal-400" />
                    </div>
                    <div>
                      <p className="text-void-300 font-medium">Team</p>
                      <p className="text-void-500 text-sm">Small, hands-on, and founder-led</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-xl bg-void-950/80 border border-void-700/30 text-center">
                  <p className="text-void-300 font-medium mb-1">Mimeograph Holdings, LLC</p>
                  <p className="text-void-500 text-sm">Owns the IP · Florida LLC, since 2016</p>
                  <p className="text-void-300 font-medium mt-3 mb-1">SynthetIQ Signals®, Inc.</p>
                  <p className="text-void-500 text-sm">Operates the platform · Florida Corp, since 2025</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

function CategoryPage({ category }) {
  const data = navigationData.corporate.categories.find(c => c.href.includes(category))
  
  if (!data) return <CorporateIndex />
  
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
              {data.name}
            </h1>
            <p className="text-xl text-void-400 mb-12">Explore {data.name.toLowerCase()} information</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {data.items.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className="block p-6 rounded-2xl bg-void-900/50 border border-void-800/50 hover:border-synth-700/50 transition-all group"
                  >
                    <h3 className="text-lg font-display font-semibold text-void-100 mb-2 group-hover:text-synth-300 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-void-400 text-sm">{item.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default function CorporatePage() {
  return (
    <Routes>
      <Route index element={<CorporateIndex />} />
      <Route path="company/*" element={<CategoryPage category="company" />} />
      <Route path="news/*" element={<CategoryPage category="news" />} />
      <Route path="legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="legal/privacy/graffiti-os" element={<GraffitiOSPrivacyPage />} />
      <Route path="legal/terms" element={<TermsPage />} />
      <Route path="legal/compliance" element={<CompliancePage />} />
      <Route path="legal/cookies" element={<CookiesPolicyPage />} />
      <Route path="legal/*" element={<CategoryPage category="legal" />} />
    </Routes>
  )
}